import React, { useState } from 'react';
import PrimaryButton from '../../components/common/PrimaryButton';
import ReusableTextField from '../../components/common/ReusableTextField';
import ReusableCheckbox from '../../components/common/ReusableCheckbox';
import { FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import '../../assets/css/WhatsAppSetup.css';

const WhatsAppConfiguration = ({ onSubmit, onCancel, initialValues = {}, loading = false, error = '' }) => {
  const [formData, setFormData] = useState({
    businessOwnerId: initialValues.businessOwnerId,
    appId: initialValues.appId || '',
    businessID: initialValues.businessID || '',
    wabaId: initialValues.wabaId || '',
    phoneNumberId: initialValues.phoneNumberId || '',
    appSecret: initialValues.appSecret || '',
    isActive: true,
    isAgreeToDPA: initialValues.isAgreeToDPA || false,
    isConsentToProcess: initialValues.isConsentToProcess || false
  });

  const [errors, setErrors] = useState({});
  const [showInfo, setShowInfo] = useState(false);
  const [showDataProcessingAgreementInfo, setShowDataProcessingAgreementInfo] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.appId.trim()) {
      newErrors.appId = 'Meta App ID is required';
    }

    if (!formData.businessID.trim()) {
      newErrors.businessID = 'Business ID is required';
    }

    if (!formData.wabaId.trim()) {
      newErrors.wabaId = 'WhatsApp Business Account ID is required';
    }

    if (!formData.phoneNumberId.trim()) {
      newErrors.phoneNumberId = 'WhatsApp phone number ID is required';
    }

    if (!formData.appSecret.trim()) {
      newErrors.appSecret = 'App Secret is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('formData', formData);
    // Consent validations
    const consentErrors = {};
    if (!formData.isAgreeToDPA) {
      consentErrors.isAgreeToDPA = 'You must agree to the Data Processing Agreement.';
    }
    if (!formData.isConsentToProcess) {
      consentErrors.isConsentToProcess = 'You must consent to the data processing.';
    }
    if (Object.keys(consentErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...consentErrors }));
      return;
    }

    if (!validateForm()) {
      return;
    }

    console.log('formData', formData);
    onSubmit(formData);
  };

  return (
    <div className="whatsapp-setup-wrapper">
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <h2 className="section-title">Configure WhatsApp Business API</h2>
        <p className="section-subtitle">
          Enter your WhatsApp Business API credentials to complete the setup.
        </p>

        {/* Error Display */}
        {error && (
          <div style={{
            backgroundColor: '#fee',
            color: '#c33',
            padding: '12px',
            borderRadius: '4px',
            marginBottom: '24px',
            border: '1px solid #fcc'
          }}>
            {error}
          </div>
        )}

        {/* Info Section */}
        <div className="setup-section" style={{ marginBottom: '32px' }}>
          <div 
            className="setup-header" 
            style={{ cursor: 'pointer' }}
            onClick={() => setShowInfo(!showInfo)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FaInfoCircle className="test-setup-info-icon" style={{ fontSize: '20px' }} />
              <h6 className="setup-title" style={{ margin: 0 }}>
                How to get these credentials
              </h6>
              {showInfo ? <FaChevronUp /> : <FaChevronDown />}
            </div>
          </div>
          
          {showInfo && (
            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <ol style={{ margin: 0, paddingLeft: '20px', color: '#666', lineHeight: '1.8' }}>
                <li>Go to <strong>Facebook Business Manager</strong></li>
                <li>Navigate to <strong>WhatsApp → API Setup</strong></li>
                <li>Copy <strong>Business ID</strong> from Business Settings → Business Info</li>
                <li>Copy <strong>App ID</strong> from App Dashboard → Settings → Basic</li>
                <li>Copy <strong>App Secret</strong> from App Dashboard → Settings → Basic</li>

                Note: You need to select/ add a phone number to the business account to get the phone number ID.
                <li>Copy <strong>WhatsApp Business account ID</strong> WhatsApp - API Setup - Whats app Business account ID</li>
                <li>Copy <strong>WhatsApp phone number ID</strong> WhatsApp - API Setup - Phone number ID</li>
                
              </ol>
            </div>
          )}
        </div>

        {/* Configuration Form */}
        <form onSubmit={handleSubmit}>
          <ReusableTextField
            id="appId"
            name="appId"
            label="Meta App ID"
            placeholder="Enter your Meta App ID"
            value={formData.appId}
            onChange={handleInputChange}
            required
            className={errors.appId ? 'is-invalid' : ''}
            helpText={errors.appId}
          />

          <ReusableTextField
            id="businessID"
            name="businessID"
            label="Business ID"
            placeholder="Enter your WhatsApp Business ID"
            value={formData.businessID}
            onChange={handleInputChange}
            required
            className={errors.businessID ? 'is-invalid' : ''}
            helpText={errors.businessID}
          />

          <ReusableTextField
            id="wabaId"
            name="wabaId"
            label="Business account ID"
            placeholder="Enter your WhatsApp Business account ID"
            value={formData.wabaId}
            onChange={handleInputChange}
            required
            className={errors.wabaId ? 'is-invalid' : ''}
            helpText={errors.wabaId}
          />

          <ReusableTextField
            id="phoneNumberId"
            name="phoneNumberId"
            label="Phone number ID"
            value={formData.phoneNumberId}
            onChange={handleInputChange}
            required
            className={errors.phoneNumberId ? 'is-invalid' : ''}
            helpText={errors.phoneNumberId}
          />

          <ReusableTextField
            id="appSecret"
            name="appSecret"
            label="App Secret"
            type="password"
            placeholder="Enter your WhatsApp App Secret"
            value={formData.appSecret}
            onChange={handleInputChange}
            required
            className={errors.appSecret ? 'is-invalid' : ''}
            helpText={errors.appSecret || 'Your secret key from Facebook App Dashboard'}
          />

          {/* WhatsApp Data Processing Agreement */}
          <div className="Data-Processing-Agreement-section" style={{ marginBottom: '32px' }}>
            <div className="Data-Processing-Agreement-header"
            onClick={() => setShowDataProcessingAgreementInfo(!showDataProcessingAgreementInfo)}
            >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FaInfoCircle className="test-setup-info-icon" style={{ fontSize: '20px' }} 
              />
              <h6 className="Data-Processing-Agreement-title" style={{ margin: 0 }}>
              WhatsApp Data Processing Agreement
              </h6>
              {showDataProcessingAgreementInfo ? <FaChevronUp /> : <FaChevronDown />}
            </div>
            </div>

            {showDataProcessingAgreementInfo && <div className="Data-Processing-Agreement-content">
            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f8f9fa', borderadius: '8px' }}>
              <ol style={{ margin: 0, paddingLeft: '20px', color: '#666', lineHeight: '1.8' }}>
              By proceeding, you authorize ProspectPulse Pvt Ltd to:
              <br />
              <p><strong>DATA PROCESSING:</strong></p>
              <ul>
                <li>Receive webhook notifications from Meta/WhatsApp</li>
                <li>Process incoming and outgoing messages</li>
                <li>Store message content and metadata</li>
                <li>Process customer contact information</li>
                <li>Generate analytics and reports</li>
                <li>Manage customer opt-outs, stop sending messages to customers who have opted out</li>
              </ul>
              <br />
              <p><strong>YOUR RESPONSIBILITIES:</strong></p>
              <ul>
                <li>Obtain customer opt-in consent</li>
                <li>Comply with WhatsApp Business Policy</li>
                <li>Follow data protection regulations</li>
              </ul>
              <br />
              <p><strong>YOUR RIGHTS:</strong></p>
              <ul>
                <li>Access your data anytime</li>
                <li>Export your data</li>
                <li>Disconnect and delete data</li>
                <li>Modify processing permissions</li>
              </ul>
              <br />
               Check our  <a href="/privacy-policy" target="_blank" rel="noreferrer">privacy policy</a> and <a href="/terms-and-conditions" target="_blank" rel="noreferrer">terms and conditions</a>
              </ol>
            </div>
          </div>}
        </div>

          {/* Consent Section */}
          <div className="consent-section" style={{ marginTop: '8px', marginBottom: '16px' }}>
            <ReusableCheckbox
              id="isAgreeToDPA"
              name="isAgreeToDPA"
              labelElement={
                <span>
                  I have read and agree to the Data Processing Agreement.
                </span>
              }
              checked={formData.isAgreeToDPA}
              onChange={(e) => {
                setFormData(prev => ({
                  ...prev,
                  isAgreeToDPA: e.target.checked
                }));
                if (errors.isAgreeToDPA) {
                  setErrors(prev => ({ ...prev, isAgreeToDPA: '' }));
                }
              }}
              helpText={errors.isAgreeToDPA}
            />

            <ReusableCheckbox
              id="isConsentToProcess"
              name="isConsentToProcess"
              label="I consent to the data processing described above"
              checked={formData.isConsentToProcess}
              onChange={(e) => {
                setFormData(prev => ({
                  ...prev,
                  isConsentToProcess: e.target.checked
                }));
                if (errors.isConsentToProcess) {
                  setErrors(prev => ({ ...prev, isConsentToProcess: '' }));
                }
              }}
              helpText={errors.isConsentToProcess}
            />
          </div>

          {/* Button Row */}
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            justifyContent: 'flex-end', 
            marginTop: '32px' 
          }}>
            <PrimaryButton 
              type="submit"
              disabled={loading}
              style={{ minWidth: '120px' }}
            >
              {loading ? 'Saving...' : 'Submit'}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WhatsAppConfiguration;

